import React from 'react';
import { Icon } from '@pelando/components';
import { Tags, BadgeDollar } from '@pelando/fontawesome/icons';
import { useRouter } from 'next/router';
import NextLink from '@/presentation/components/NextLink';
import { Store, StoreDeal } from '../../../domain/entities/Store';
import useCameFromClickout from '../../hooks/useCameFromClickout';
import {
  StoreTabsContainer,
  StoreTabsContent,
  StoreTabsHeader,
  Tab,
  TabContentWrapper,
} from './style';
import { VoteVariables } from '../../../infra/api/votes/types/Vote';
import { RemoveVoteVariables } from '../../../infra/api/votes/types/RemoveVote';
import {
  StorePromotions,
  StorePromotionsQueryVariables,
} from '../../../infra/api/stores/types/IstorePromotionsQueryDTO';
import { VoteWithTemperature } from '../../../infra/api/votes/types/UserOfferVotes';
import { useTranslation } from '../../hooks/useTranslation';
import { StoresTabQueryParam, getCouponsUrl } from '../../services/urls';
import StoreCouponsTabContent from './StoreCouponsTabContent';
import StoreDealsTabContent from './StoreDealsTab';
import { useStoreTabHandler } from './hook/useStoreTabsHandler';

export const STORE_MERCADO_LIVRE = 'mercadolivre.com.br';

type StoreTabsStore = Pick<
  Store,
  'id' | 'name' | 'url' | 'couponsCount' | 'offersCount' | 'slug'
>;

export type StoreTabsProps = {
  store: StoreTabsStore;
  loading?: boolean;
  storeDeals?: StorePromotions;
  storeCoupons?: StoreDeal[];
  userOffersVote?: Record<string, VoteWithTemperature>;
  currentUserId?: string;
  onVote: (variables: VoteVariables) => void;
  onRemoveVote: (variables: RemoveVoteVariables) => void;
  getStorePromotions: (variables: StorePromotionsQueryVariables) => void;
  initialTab: StoresTabQueryParam;
};

const StoreTabs = ({
  store,
  storeDeals,
  loading,
  storeCoupons,
  userOffersVote,
  currentUserId,
  getStorePromotions,
  onRemoveVote,
  onVote,
  initialTab,
}: StoreTabsProps) => {
  const { slug, couponsCount, offersCount, id } = store;
  const { locale } = useRouter();
  const { t } = useTranslation('coupons');
  const { tab, updateTab } = useStoreTabHandler(initialTab);

  const couponText = couponsCount === 1 ? t('tab-coupon') : t('tab-coupons');
  const dealText = offersCount === 1 ? t('tab-promotion') : t('tab-promotions');

  const cameFromClickout = useCameFromClickout();

  const isDealsActive = tab === StoresTabQueryParam.DEALS;

  const onCouponTabClick = () => {
    updateTab(StoresTabQueryParam.COUPONS);
  };

  const onDealsTabClick = () => {
    updateTab(StoresTabQueryParam.DEALS);
  };

  return (
    <StoreTabsContainer>
      <StoreTabsHeader role="tablist">
        <NextLink
          id="store-coupons-tab"
          role="tab"
          aria-selected={!isDealsActive}
          aria-controls="store-coupons"
          href={getCouponsUrl({
            storeSlug: slug,
            tab: StoresTabQueryParam.COUPONS,
            locale,
          })}
          as={getCouponsUrl({
            storeSlug: slug,
            locale,
          })}
          onClick={onCouponTabClick}
        >
          <Tab data-selected={!isDealsActive}>
            <Icon icon={Tags} />
            {couponsCount} {couponText}
          </Tab>
        </NextLink>
        <NextLink
          id="store-deals-tab"
          role="tab"
          aria-controls="store-deals"
          aria-selected={isDealsActive}
          href={getCouponsUrl({
            storeSlug: slug,
            tab: StoresTabQueryParam.DEALS,
            locale,
          })}
          onClick={onDealsTabClick}
        >
          <Tab data-selected={isDealsActive}>
            <Icon icon={BadgeDollar} />
            {offersCount} {dealText}
          </Tab>
        </NextLink>
      </StoreTabsHeader>
      <StoreTabsContent>
        {isDealsActive ? (
          <TabContentWrapper
            id="store-deals"
            role="tabpanel"
            aria-labelledby="store-deals-tab"
          >
            <StoreDealsTabContent
              isLoading={loading}
              storeDeals={storeDeals}
              getStorePromotions={getStorePromotions}
              storeId={id}
              userOffersVote={userOffersVote}
              onVote={onVote}
              onRemoveVote={onRemoveVote}
            />
          </TabContentWrapper>
        ) : (
          <TabContentWrapper
            id="store-coupons"
            role="tabpanel"
            aria-labelledby="store-coupons-tab"
          >
            <StoreCouponsTabContent
              storeCoupons={storeCoupons}
              revealCoupons={cameFromClickout}
              currentUserId={currentUserId}
            />
          </TabContentWrapper>
        )}
      </StoreTabsContent>
    </StoreTabsContainer>
  );
};

export default StoreTabs;
